"use client";

import "./HighlightOverlay.css";

/**
 * Escape special regex characters
 */
function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Sanitize text for safe HTML display - escapes all HTML special characters
 * This prevents XSS by ensuring no HTML tags can be injected
 */
function sanitizeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export interface AgentInfo {
  id: string;
  name: string;
  slug: string;
  description: string;
  category: string;
  avatar?: string;
}

export interface SkillInfo {
  id: string;
  name: string;
  slug: string;
  description: string;
  category: string;
  icon?: string;
  color?: string;
}

/**
 * Generate highlighted HTML for the text overlay
 * Note: This function only uses sanitized, escaped content
 */
export function generateHighlightedContent(
  text: string,
  agents: AgentInfo[],
  skills: SkillInfo[]
): string {
  if (!text) return "";

  // Build patterns for agents (@slug) and skills (/slug)
  const agentSlugs = agents.map((a) => escapeRegExp(a.slug)).join("|");
  const skillSlugs = skills.map((s) => escapeRegExp(s.slug)).join("|");

  // First, sanitize the entire text to prevent XSS
  let result = sanitizeHtml(text);

  // Highlight @agent mentions (using sanitized text)
  if (agentSlugs) {
    const agentPattern = new RegExp(`(^|\\s)(@(${agentSlugs}))(?=\\s|$)`, "gi");
    result = result.replace(
      agentPattern,
      '$1<span class="highlight-mention">$2</span>'
    );
  }

  // Highlight /skill commands (using sanitized text)
  if (skillSlugs) {
    const skillPattern = new RegExp(`(^|\\s)(/(${skillSlugs}))(?=\\s|$)`, "gi");
    result = result.replace(
      skillPattern,
      '$1<span class="highlight-skill">$2</span>'
    );
  }

  // Convert newlines to <br> for proper display
  result = result.replace(/\n/g, "<br>");

  return result;
}
